import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Button } from '..';
import { ImageBrokenKey } from '../../assets/images';
import { deleteGuest, patchGuest, accessTokenExpired } from '../../store/actions';
import { InviteGuestItem } from '../../types/invite.type';
import { TranslationUtils, ValidationUtils, AuthUtils } from '../../utils';
import './ActionInviteGuestModal.scss';
import segmentService from '../../services/segment/segmentService';
import ddLogger from '../../logging/ddLogger';

interface ActionInviteGuestModalProps {
    item: InviteGuestItem;
    show: boolean;
    handleClose: () => any;
    currentGuestCount: number;
    reservationId: string;
}

interface SecondaryGuest {
    reservationId?: number;
    guestId: number;
    firstName: string;
    lastName: string;
    email: string;
}

export const ActionInviteGuestModal: React.FC<ActionInviteGuestModalProps> = (props) => {
    const dispatch = useDispatch();
    const { item, show, handleClose, currentGuestCount, reservationId } = props;

    const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
    const [isRemoveDisabled, setIsRemoveDisabled] = useState(false);
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const [isLoadingRemove, setIsLoadingRemove] = useState(false);
    const [secondaryGuest, setSecondaryGuest] = useState<SecondaryGuest>(item);

    useEffect(() => {
        const guest: SecondaryGuest = { guestId: item.guestId, firstName: item.firstName, lastName: item.lastName, email: item.email };
        setSecondaryGuest(guest);
        setIsSubmitDisabled(!ValidationUtils.isValidGuestForm(secondaryGuest));
    }, [item]);

    useEffect(() => {
        setIsSubmitDisabled(!ValidationUtils.isValidGuestForm(secondaryGuest));
    }, [secondaryGuest]);

    const handleEditInvite = async () => {
        if (AuthUtils.isAccessTokenSessionExpired()) {
            handleClose();
            dispatch(accessTokenExpired(true));
            return;
        }

        setIsSubmitDisabled(true);
        setIsLoadingSubmit(true);
        const editInvite: any = {
            type: 'reservation-guests',
            attributes: {
                first_name: secondaryGuest.firstName,
                last_name: secondaryGuest.lastName,
                email: secondaryGuest.email,
            },
        };
        await dispatch(patchGuest(secondaryGuest.guestId, editInvite));
        segmentService.trackEvent('Trip Portal Guest Invites', {
            action: 'Guest Information Updated',
            guest_count: currentGuestCount,
        });
        ddLogger.info(`Guest Information Updated. ReservationId: ${reservationId}`, { guest_count: currentGuestCount });
        goToInit();
    };

    const handleRemoveInvite = async () => {
        if (AuthUtils.isAccessTokenSessionExpired()) {
            handleClose();
            dispatch(accessTokenExpired(true));
            return;
        }

        setIsRemoveDisabled(true);
        setIsLoadingRemove(true);
        await dispatch(deleteGuest(secondaryGuest.guestId));
        segmentService.trackEvent('Trip Portal Guest Invites', {
            action: 'Guest Removed',
            guest_count: currentGuestCount - 1,
        });
        ddLogger.info(`Guest Removed. ReservationId: ${reservationId}`, { guest_count: currentGuestCount - 1 });
        goToInit();
    };

    const handleOnChange = (e: any, property: 'firstName' | 'lastName' | 'email') => {
        const editGuest: SecondaryGuest = { ...secondaryGuest };
        editGuest[property] = e.target.value;
        setSecondaryGuest(editGuest);
    };

    const goToInit = () => {
        setIsSubmitDisabled(false);
        setIsRemoveDisabled(false);
        setIsLoadingSubmit(false);
        setIsLoadingRemove(false);
        handleClose();
    };

    const showModalRemoveInvite = () => {
        const divRemove = document.getElementById('modal-remove');
        const divEdit = document.getElementById('modal-edit');

        if (!divRemove || !divEdit) {
            return;
        }
        divRemove.classList.remove('hidden');
        divEdit.classList.add('hidden');
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton />
            <div id="modal-edit">
                <div className="invite-guest-modal">
                    <div className="invite-guest-modal-text">
                        <h4>{TranslationUtils.get('edit_guest')}</h4>
                        <p>{TranslationUtils.get('edit_guest_note_modal')}</p>
                    </div>
                    <div className="row row-5">
                        <div className="col-12 col-md-6">
                            <input
                                className="form-control input-padding"
                                id="FirstName"
                                value={secondaryGuest.firstName}
                                onChange={(e) => handleOnChange(e, 'firstName')}
                                type="text"
                                placeholder={TranslationUtils.get('first_name')}
                            />
                        </div>
                        <div className="col-12 col-md-6">
                            <input
                                className="form-control input-padding"
                                id="LastName"
                                value={secondaryGuest.lastName}
                                onChange={(e) => handleOnChange(e, 'lastName')}
                                type="text"
                                placeholder={TranslationUtils.get('last_name')}
                            />
                        </div>
                        <div className="col-12">
                            <input
                                className="form-control input-padding"
                                id="Email"
                                value={secondaryGuest.email}
                                onChange={(e) => handleOnChange(e, 'email')}
                                type="text"
                                placeholder={TranslationUtils.get('email_address')}
                            />
                        </div>
                    </div>
                    <div className="invite-guest-modal-buttons">
                        <Button
                            id="button-remove-confirm-modal"
                            variant="default"
                            label={TranslationUtils.get('remove_guest')}
                            onClick={showModalRemoveInvite}
                        />
                        <Button
                            aria-label={TranslationUtils.get('submit_guest_edition')}
                            id="button-edit-invite-guest"
                            variant="primary"
                            isLoading={isLoadingSubmit}
                            disabled={isSubmitDisabled}
                            label={TranslationUtils.get('edit_guest')}
                            onClick={() => handleEditInvite()}
                        />
                    </div>
                </div>
            </div>
            <div id="modal-remove" className="hidden">
                <div className="remove-guest-modal">
                    <ImageBrokenKey className="remove-modal-image text-center d-none d-lg-block" />
                    <div className="remove-guest-modal-text">
                        <h4>{TranslationUtils.get('are_you_sure')}</h4>
                        <p>
                            {`${secondaryGuest.firstName} ${secondaryGuest.lastName}`} {TranslationUtils.get('remove_invite_note')}
                        </p>
                    </div>
                    <div className="invite-guest-modal-buttons">
                        <Button
                            id="button-close-remove-guest-modal"
                            variant="default"
                            label={TranslationUtils.get('no_remove_invited')}
                            onClick={handleClose}
                        />
                        <Button
                            aria-label={`${TranslationUtils.get('confirm_guest_remove')} ${secondaryGuest.firstName} ${
                                secondaryGuest.lastName
                            }`}
                            id="button-remove-guest-modal"
                            isLoading={isLoadingRemove}
                            disabled={isRemoveDisabled}
                            variant="primary"
                            label={TranslationUtils.get('yes_remove_invited')}
                            onClick={() => handleRemoveInvite()}
                        />
                    </div>
                </div>
            </div>
        </Modal>
    );
};
